import { ArrowLeft, ChevronDown } from 'lucide-react'
import { Link, Navigate, useParams } from 'react-router-dom'
import { useEffect, useMemo, useState } from 'react'
import { Badge, WorkspaceSkeleton } from '../components/UI'
import { useTeamScope } from '../components/TeamScope'
import {
  formatDuration,
  loadEmployeeHistory,
  loadPersonOperations,
  shortThreadId,
  type EmployeeDailyRecord,
  type TeamPerson
} from '../lib/liveOperations'

type HistoryView = 'daily' | 'weekly' | 'monthly'
type RecordStats = {
  worked: number
  replied: number
  waiting: number
  responseRate: number | null
  averageResponseSeconds: number | null
}

const dayKey = (value: string) => String(value).slice(0, 10)

function dayLabel(key: string) {
  return new Date(`${key}T12:00:00`).toLocaleDateString(undefined, {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    year: 'numeric'
  })
}

function monthLabel(key: string) {
  const [year, month] = key.split('-').map(Number)
  return new Date(year, month - 1, 1).toLocaleDateString(undefined, {
    month: 'long',
    year: 'numeric'
  })
}

function weekOfMonth(value: string) {
  return Math.floor((Number(dayKey(value).slice(8, 10)) - 1) / 7) + 1
}

function average(values: Array<number | null | undefined>) {
  const numbers = values.filter((value): value is number => value != null && Number.isFinite(value))
  return numbers.length ? Math.round(numbers.reduce((sum, value) => sum + value, 0) / numbers.length) : null
}

function stats(records: EmployeeDailyRecord[]): RecordStats {
  const replied = records.filter(record => record.work_status === 'replied').length
  return {
    worked: records.length,
    replied,
    waiting: records.length - replied,
    responseRate: records.length ? Math.round((replied / records.length) * 100) : null,
    averageResponseSeconds: average(records.map(record => record.arrival_to_reply_sent_seconds))
  }
}

function StatsRow({ value }: { value: RecordStats }) {
  return <div className="records-stat-grid">
    <div><span>Customer emails worked</span><strong>{value.worked}</strong></div>
    <div><span>Replies sent</span><strong>{value.replied}</strong></div>
    <div><span>Needs follow-through</span><strong>{value.waiting}</strong></div>
    <div><span>Response rate</span><strong>{value.responseRate == null ? '—' : `${value.responseRate}%`}</strong></div>
    <div><span>Average response</span><strong>{formatDuration(value.averageResponseSeconds)}</strong></div>
  </div>
}

function DayRecords({ day, records }: { day: string; records: EmployeeDailyRecord[] }) {
  const value = stats(records)

  return <details className="records-day" open>
    <summary>
      <div>
        <strong>{dayLabel(day)}</strong>
        <span>{records.length} measured customer email{records.length === 1 ? '' : 's'} · {value.replied} replies sent</span>
      </div>
      <ChevronDown size={17}/>
    </summary>

    <div className="records-day-body">
      <StatsRow value={value}/>

      <div className="records-email-list">
        {records.map(record => (
          <div className="records-email-row" key={`${day}-${record.external_thread_id}`}>
            <div>
              <strong>Email reference {shortThreadId(record.external_thread_id)}</strong>
              <span>
                {record.inbound_received_at
                  ? `Received ${new Date(record.inbound_received_at).toLocaleTimeString([], { hour:'2-digit', minute:'2-digit' })}`
                  : 'Received time unavailable'}
              </span>
            </div>
            <div><span>Response time</span><strong>{formatDuration(record.arrival_to_reply_sent_seconds)}</strong></div>
            <div><span>Reply status</span><strong>{record.reply_sent_at ? new Date(record.reply_sent_at).toLocaleTimeString([], { hour:'2-digit', minute:'2-digit' }) : 'Waiting'}</strong></div>
            <Badge tone={record.work_status === 'replied' ? 'good' : 'neutral'}>
              {record.work_status === 'replied' ? 'Replied' : 'Needs follow-through'}
            </Badge>
          </div>
        ))}
      </div>
    </div>
  </details>
}

export function EmployeeRecords() {
  const { selectedTeamId } = useTeamScope()
  const { id, view: routeView } = useParams()
  const view: HistoryView = routeView === 'weekly' || routeView === 'monthly' ? routeView : 'daily'

  const [person, setPerson] = useState<TeamPerson | null>(null)
  const [records, setRecords] = useState<EmployeeDailyRecord[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')

  useEffect(() => {
    if (!id) return
    Promise.all([loadPersonOperations(id, selectedTeamId), loadEmployeeHistory(id)])
      .then(([personData, history]) => {
        setPerson(personData.person)
        setRecords(history)
      })
      .catch(err => setError(err instanceof Error ? err.message : 'Employee records could not be loaded.'))
      .finally(() => setLoading(false))
  }, [id, selectedTeamId])

  const days = useMemo(() => {
    const grouped = new Map<string, EmployeeDailyRecord[]>()
    for (const record of records) {
      const key = dayKey(record.activity_day)
      grouped.set(key, [...(grouped.get(key) ?? []), record])
    }
    return [...grouped.entries()].sort(([a], [b]) => b.localeCompare(a))
  }, [records])

  const weeks = useMemo(() => {
    const grouped = new Map<string, { month: string; week: number; days: Map<string, EmployeeDailyRecord[]> }>()
    for (const [day, dayRecords] of days) {
      const month = day.slice(0, 7)
      const week = weekOfMonth(day)
      const key = `${month}-W${week}`
      const item = grouped.get(key) ?? { month, week, days: new Map<string, EmployeeDailyRecord[]>() }
      item.days.set(day, dayRecords)
      grouped.set(key, item)
    }
    return [...grouped.entries()].sort(([a], [b]) => b.localeCompare(a))
  }, [days])

  const months = useMemo(() => {
    const grouped = new Map<string, Map<string, EmployeeDailyRecord[]>>()
    for (const [day, dayRecords] of days) {
      const month = day.slice(0, 7)
      const monthDays = grouped.get(month) ?? new Map<string, EmployeeDailyRecord[]>()
      monthDays.set(day, dayRecords)
      grouped.set(month, monthDays)
    }
    return [...grouped.entries()].sort(([a], [b]) => b.localeCompare(a))
  }, [days])

  if (!id) return <Navigate to="/app/team" replace/>
  if (loading) return <WorkspaceSkeleton rows={5}/>
  if (!person) return <Navigate to="/app/team" replace/>

  return <div className="stack-lg records-page">
    <div className="detail-page-nav">
      <Link to={`/app/team/${person.userId}`} className="back-link"><ArrowLeft size={17}/>Back to {person.displayName}</Link>
    </div>

    <section className="records-hero">
      <div>
        <span className="eyebrow">Employee {view} records · {person.teamName}</span>
        <h2>{person.displayName}</h2>
        <p>
          {view === 'daily'
            ? 'Every measured operating day and the customer-email records inside each day.'
            : view === 'weekly'
              ? 'Weekly customer-email results, grouped inside each month with operating days underneath.'
              : 'Month-by-month customer-email results with every measured operating day underneath.'}
        </p>
      </div>
      <Badge tone="blue">{records.length} records</Badge>
    </section>

    {error && <div className="notice notice-inline"><strong>Records unavailable:</strong><span>{error}</span></div>}

    {records.length === 0 ? (
      <section className="records-empty">
        <strong>No employee history yet</strong>
        <span>Operating days will appear here as Mail Percent measures this employee’s customer-email work.</span>
      </section>
    ) : view === 'daily' ? (
      <div className="records-period-list">
        {days.map(([day, dayRecords]) => <DayRecords key={day} day={day} records={dayRecords}/>)}
      </div>
    ) : view === 'weekly' ? (
      <div className="records-period-list">
        {weeks.map(([key, group]) => {
          const allRecords = [...group.days.values()].flat()
          return <section className="records-period-card" key={key}>
            <div className="records-period-head">
              <div><span className="eyebrow">{monthLabel(group.month)}</span><h3>Week {group.week}</h3></div>
              <span>{group.days.size} operating day{group.days.size === 1 ? '' : 's'}</span>
            </div>
            <StatsRow value={stats(allRecords)}/>
            <div className="records-nested-days">
              {[...group.days.entries()].sort(([a],[b]) => b.localeCompare(a)).map(([day, dayRecords]) =>
                <DayRecords key={day} day={day} records={dayRecords}/>
              )}
            </div>
          </section>
        })}
      </div>
    ) : (
      <div className="records-period-list">
        {months.map(([month, monthDays]) => {
          const allRecords = [...monthDays.values()].flat()
          return <section className="records-period-card" key={month}>
            <div className="records-period-head">
              <div><span className="eyebrow">Monthly record</span><h3>{monthLabel(month)}</h3></div>
              <span>{monthDays.size} operating day{monthDays.size === 1 ? '' : 's'}</span>
            </div>
            <StatsRow value={stats(allRecords)}/>
            <div className="records-nested-days">
              {[...monthDays.entries()].sort(([a],[b]) => b.localeCompare(a)).map(([day, dayRecords]) =>
                <DayRecords key={day} day={day} records={dayRecords}/>
              )}
            </div>
          </section>
        })}
      </div>
    )}
  </div>
}
